import type { SpeedMarket } from '@/features/tokenPairs';
import { tokenAmount } from '@/features/trading/utils/tradeFormat';
import { type PublicTapeTrade, USDC_DECIMALS } from '@/lib/api/speed';

/**
 * What an open position on the tape is worth right now.
 *
 * A closed row carries the reconciler's realized `pnl`; an open one carries
 * nothing, so four rows in five read "OPEN" with no number — the one thing a
 * viewer actually wants to know about a position someone is still holding.
 *
 * The row already has everything but the current price:
 *
 *     unrealized = (mark - entry) x qty x (long ? 1 : -1)
 *
 * `qty` is the position size in COIN units, which is what makes this work
 * without a per-trade exchange call. Verified against production rows: the
 * implied notional `entry x qty` lands within a fraction of a percent of
 * `margin x leverage` on every open trade (BTC 40x: 170.18 vs 170.80; PONS 3x:
 * 570.17 vs 571.20), the gap being entry slippage and size rounding.
 *
 * It is an ESTIMATE and must always be shown as one. It excludes funding
 * accrued since the open, and our own fees (openFee / serviceFee / closeFee /
 * holdingFee are separate columns the tape never sees). It is "how is the
 * trade doing", not "what will they be paid".
 */
export function unrealizedPnl(
    trade: PublicTapeTrade,
    markPx: number | null | undefined,
): number | null {
    if (markPx === null || markPx === undefined) return null;
    if (!Number.isFinite(markPx) || markPx <= 0) return null;
    if (trade.isLong === null) return null;

    const entry = Number(trade.entryPx);
    const qty = Number(trade.qty);
    // qty is absolute; a short is expressed by isLong, never by a negative size
    if (!Number.isFinite(entry) || entry <= 0) return null;
    if (!Number.isFinite(qty) || qty <= 0) return null;

    return (markPx - entry) * qty * (trade.isLong ? 1 : -1);
}

/**
 * Mark prices by coin, for the whole tape in one lookup.
 *
 * Keyed on the UPPERCASED coin and nothing else. The tape stores coins
 * lowercased (`zec`, `xyz:spcx`) while the markets list does not (`ZEC`,
 * `xyz:SPCX`), so a case-sensitive match silently returns nothing — every open
 * row falls back to no figure and the feature looks like it simply failed.
 *
 * The `dex` column is deliberately NOT part of the key. A builder market
 * already carries its namespace inside the coin (`xyz:GOLD`), so the coin alone
 * is unique across dexes; folding `dex` in adds a way for the two sides to
 * disagree about null and match nothing.
 */
export function markPricesByCoin(
    markets: readonly SpeedMarket[] | undefined,
): Map<string, number> {
    const marks = new Map<string, number>();
    for (const market of markets ?? []) {
        const price = Number(market.markPx);
        if (Number.isFinite(price) && price > 0) {
            marks.set(market.coin.toUpperCase(), price);
        }
    }
    return marks;
}

/** The mark for a tape row, or null when that market is not listed. */
export function markForTrade(
    trade: PublicTapeTrade,
    marks: Map<string, number>,
): number | null {
    if (!trade.coin) return null;
    return marks.get(trade.coin.toUpperCase()) ?? null;
}

/**
 * PnL as a percentage of what the trader put up.
 *
 * Return on COLLATERAL, matching `exchangePnlPercent` in the trading utils and
 * therefore what a trader sees on their own position: a $0.12 loss is -2.8% on
 * a 40x BTC position with $4.27 of margin, not the -0.07% the price moved. On a
 * feed where leverage runs from 3x to 40x, the dollar figure alone makes a
 * reckless trade and a careful one look alike.
 *
 * `margin` arrives in USDC base units, as stored on the trade row.
 */
export function roiPercent(
    pnlUsd: number | null,
    marginRaw: string | null,
): number | null {
    if (pnlUsd === null || !Number.isFinite(pnlUsd)) return null;
    const margin = tokenAmount(marginRaw, USDC_DECIMALS);
    // no margin recorded, or a zero one: a percentage of nothing is not 0%
    if (margin === null || margin <= 0) return null;
    return (pnlUsd / margin) * 100;
}
